<template>
  <div class="flex flex-col xl:flex-row w-full">
    <div class="basis-1/2 mx-2">
      <!-- BASIC INFO SECTION -->
      <Fieldset legend="Project Details" class="my-3">
        <div class="flex flex-col">
          <div class="flex flex-row">
            <StackedLabelInput name="projectCode" label="Project ID" classes="required" size="basis-1/2 px-2" />
            <StackedLabelInput name="projectName" label="Project Name" classes="required" size="basis-1/2 px-2" />
          </div>
          <div class="flex flex-row">
            <StackedLabelDropdown
              name="propertyType"
              label="Property Type"
              size="basis-1/3 px-2 mt-3"
              :options="propertyTypes"
              options-label="name"
              :showClear="true"
              placeholder="Select a property type"
            />
            <StackedLabelDropdown
              name="propertyTypeEx"
              label="Property Type 2"
              size="basis-1/3 px-2 mt-3"
              :options="propertyTypesEx"
              options-label="name"
              :showClear="true"
              placeholder="Select a property type"
            />
            <StackedLabelDropdown
              name="userGroup"
              label="User Group"
              size="basis-1/3 px-2 mt-3"
              :options="userGroups"
              :disabled="userGroups.length <= 1"
              options-label="name"
              placeholder="Select a user group"
              classes="required"
              @change="userGroupUpdated"
            />
          </div>
          <div class="flex flex-row">
            <StackedLabelDropdown
              name="projectStatus"
              label="Status"
              size="basis-1/3 px-2 mt-3"
              :options="projectStatuses"
              options-label="name"
              @change="statusChanged"
            />
            <StackedLabelDropdown
              name="checkingAccount"
              label="Core Ledger"
              size="basis-1/3 px-2 mt-3"
              :options="checkingAccounts"
              :disabled="values.userGroup == null"
              options-label="description"
              placeholder="Select a core ledger"
              showClear
            />
            <StackedLabelDropdown
              name="subdivision"
              label="Group (Subdivision)"
              size="basis-1/3 px-2 mt-3"
              :options="subdivisions"
              :disabled="values.userGroup == null"
              options-label="subdivisionName"
              placeholder="Select a group"
              showClear
              @vue:updated="verifySubdivision"
            />
          </div>
          <div class="flex flex-row">
            <StackedLabelDatePicker
              name="soldDate"
              label="Sold Date"
              size="basis-1/3 px-2 mt-3"
              :disabled="values.projectStatus && values.projectStatus.category !== ProjectStatusCategory.SOLD"
              :show-button-bar="false"
            ></StackedLabelDatePicker>
          </div>
        </div>
      </Fieldset>

      <!-- ADDRESS SECTION -->
      <Fieldset legend="Address" class="my-3">
        <div class="flex flex-col">
          <div class="flex flex-row">
            <StackedLabelInput name="address" label="Property Address" size="flex-initial basis-2/3 px-2" />
            <StackedLabelInput name="county" label="County" size="flex-none basis-1/3 px-2" />
          </div>
          <div class="flex flex-row mt-3">
            <StackedLabelInput name="city" label="City" size="flex-none basis-1/3 px-2" />
            <StateSelect name="state" label="State" size="basis-1/3 px-2" />
            <StackedLabelInput name="zip" label="Zip" size="flex-none basis-1/3 px-2" />
          </div>
        </div>
      </Fieldset>
    </div>

    <!-- ENTITY SECTION -->
    <div class="basis-1/2 mx-2">
      <Fieldset legend="Entity Information" class="my-3">
        <div class="flex flex-col">
          <div class="flex flex-row">
            <EntityLookup name="processor" label="Processor Name" v-model="entities" size="px-2 basis-1/2" classes="required"></EntityLookup>
            <!-- :disabled="!isDrawZero && !permissions.modifyAfterDrawZero" -->
            <EntityLookup name="lender" label="Primary Lender" v-model="entities" size="px-2 basis-1/2" classes="required"></EntityLookup>
            <!-- :disabled="!isDrawZero && !permissions.modifyAfterDrawZero" -->
          </div>
          <div class="flex flex-row mt-3">
            <EntityLookup name="borrower" label="Primary Borrower" v-model="entities" size="px-2 basis-1/2" classes="required"></EntityLookup>
            <!-- :disabled="!isDrawZero && !permissions.modifyAfterDrawZero" -->
            <EntityLookup name="builder" label="Primary Builder" v-model="entities" size="px-2 basis-1/2" classes="required"></EntityLookup>
            <!-- :disabled="!isDrawZero && !permissions.modifyAfterDrawZero" -->
          </div>
          <div class="flex flex-row mt-3">
            <EntityLookup name="inspector" label="Primary Inspector" v-model="entities" size="px-2 basis-1/2" classes="required"></EntityLookup>
            <!-- :disabled="!isDrawZero && !permissions.modifyAfterDrawZero" -->
          </div>
        </div>
      </Fieldset>
      <!-- OPTIONS SECTION -->
      <Fieldset legend="Project Settings" class="my-3">
        <div class="flex flex-col gap-5">
          <div class="flex flex-row gap-5">
            <StackedLabelDropdown
              name="defaultPayAction"
              label="Default Pay Action"
              :options="defaultPayActions"
              :disabled="values.checkingAccount == null"
              options-label=""
              placeholder="Select a group"
              classes="required"
            />
            <LabeledCheckboxNoForm
              v-model="projectSettings.automaticDefaultPayments"
              name="automaticDefaultPayments"
              label="Automatically Pay Default in Full"
              class="mt-auto mb-2"
              @update:model-value="automaticDefaultPaymentsChanged"
            ></LabeledCheckboxNoForm>
          </div>
          <div class="flex flex-row gap-5">
            <LabeledCheckboxNoForm
              v-if="isClient(UMB)"
              v-model="projectSettings.showBorrowerName"
              name="showBorrowerName"
              label="Display Borrower Name"
              class="mt-auto mb-2"
            ></LabeledCheckboxNoForm>
          </div>
        </div>
      </Fieldset>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isClient, NBH, UMB } from "@/services/Client";
import type { ProjectInfo, ProjectSettings } from "@/types/Projects";
import type { Entity } from "@/types/Entity";
import type { Code } from "@/types/Codes";
import type { CheckingAccount } from "@/types/CheckingAccount";
import type { Subdivision } from "@/types/Subdivision";
import type { UserPermissions } from "@/types/User";
import type { ProjectStatus } from "@/types/ProjectStatus";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";

const projectSettings = defineModel<ProjectSettings>("projectSettings", { required: true });
const entities = defineModel<Entity[]>("entities", { required: true });

const emit = defineEmits<{
  userGroupUpdated: [];
}>();

interface Props {
  propertyTypes: Code[];
  propertyTypesEx: Code[];
  userGroups: Code[];
  checkingAccounts: CheckingAccount[];
  subdivisions: Subdivision[];
  values: ProjectInfo;
  permissions: UserPermissions;
  isDrawZero: boolean;
  projectStatuses: ProjectStatus[];
  setFieldValue: any;
}
const props = defineProps<Props>();

const userGroupUpdated = () => {
  emit("userGroupUpdated");
};

const defaultPayActions = ref<String[]>(isClient(NBH) ? ["Fed Wire", "Transfers"] : ["Checks", "Fed Wire", "Transfers"]);

const automaticDefaultPaymentsChanged = (): void => {
  if (projectSettings.value.automaticDefaultPayments) {
    projectSettings.value.autoDisburseFunds = true;
  }
};

const statusChanged = (): void => {
  if (props.values.projectStatus.category === ProjectStatusCategory.SOLD && props.values.soldDate == null) {
    props.setFieldValue("soldDate", new Date());
  } else if (props.values.projectStatus.category !== ProjectStatusCategory.SOLD) {
    props.setFieldValue("soldDate", null);
  }
};

const verifySubdivision = (): void => {
  if (props.values.subdivision == null) return;

  if (props.values.userGroup.num != props.values.subdivision.userGroup.num) {
    error(
      `Invalid Subdivision ${props.values.subdivision.subdivisionName}`,
      `Subdivision user group (${props.values.subdivision.userGroup.name}) and project user group do not match`
    );
    props.setFieldValue("subdivision", null);
  }
};
</script>
